import {
  ClockIcon,
  CrossIcon,
  PlusIcon,
  ThreeDots,
  UserContributorIcon,
  UserGuestIcon,
} from 'assets/icons';
import { Button, ButtonSize, ButtonStyle } from 'common/button';
import { ThreeStatesCheckbox } from 'common/checkbox-three-states';
import { Breakpoints } from 'common/enums';
import { InvitationStep } from 'common/invite-to-record-process/invite-to-record-process';
import { Menu } from 'common/menu';
import { PaginationSelect } from 'common/paginator-select';
import {
  collaboratorsActions,
  CollaboratorsContext,
} from 'context/collaborators-context';
import { classnames } from 'helpers/utils';
import { useClickOutside } from 'hooks/click-outside';
import { useMediaQuery } from 'hooks/use-media-query';
import React, { useContext, useRef, useState } from 'react';
import { NoCollaboratorsFound } from '../no-collaborators-found';
import styles from './collaborators-content.module.scss';
import { VaultCollaborators } from './components/vault-collaborators';
import { useSelectVaultsCollaborators } from './components/vault-collaborators/hooks/use-select-vaults-collaborators';
import { RemoveCollaboratorsModal } from './components/remove-collaborators-modal';

type CollaboratorsContentProps = {
  vaults: VaultCollaboratorsType[];
  user: UserType;
  notifyShowInviteToRecordProcess: (step: InvitationStep) => void;
  notifyExportAll: (contributors?: string, guests?: string) => void;
  notifyResendPendingInvites: (
    inviterParam: ResendPendingInvitesQueryParams,
  ) => void;
  notifySeeMyPendingInvites: () => void;
  ownedInvites: number;
  totalPendingInvites: number;
  /** Reloads the open people lists (after inviting). */
  peopleRefreshKey?: number;
};

const CollaboratorsContent: React.FC<CollaboratorsContentProps> = ({
  vaults,
  user,
  notifyShowInviteToRecordProcess,
  notifyExportAll,
  notifyResendPendingInvites,
  notifySeeMyPendingInvites,
  ownedInvites,
  totalPendingInvites,
  peopleRefreshKey = 0,
}) => {
  const {
    vaultsSelection,
    onSelectVault,
    onSelectAll,
    clearSelection,
    amountSelected,
  } = useSelectVaultsCollaborators(vaults);
  const {
    state: { checkboxFilters, vaultsCollaborators, pagination, queryParams },
    dispatch: collaboratorsDispatch,
  } = useContext(CollaboratorsContext);

  const [inviteMenuVisible, setInviteMenuVisible] = useState(false);
  const [removeCollaboratorsModalVisible, setRemoveCollaboratorsModalVisible] =
    useState(false);
  const [showPendingInvitesOptions, setShowPendingInvitesOptions] =
    useState(false);
  const [hasSendAllMyInvites, setHasSendAllMyInvites] = useState(false);
  const [hasSendAllInvites, setHasSendAllInvites] = useState(false);
  const [exportRemoveMenuVisible, setExportRemoveMenuVisible] = useState(false);
  let sendAllInvitesButtonText: string;
  if (hasSendAllMyInvites) {
    sendAllInvitesButtonText = 'Invite(s) sent!';
  } else if (ownedInvites) {
    sendAllInvitesButtonText = 'Resend my pending invites';
  } else {
    sendAllInvitesButtonText = 'Resend all invites';
  }

  const pendingInvitesMenuRef = React.useRef<HTMLDivElement>(null);

  const inviteContainerRef = useRef<HTMLDivElement>(null);

  useClickOutside(pendingInvitesMenuRef, () =>
    setShowPendingInvitesOptions(false),
  );

  useClickOutside(inviteContainerRef, () => setInviteMenuVisible(false));

  const isMobile = useMediaQuery(`(max-width: ${Breakpoints.xs}px)`);
  const isAnySelected = vaultsSelection.some(
    (vault) => vault.selected || vault.collaboratorsSelected.length > 0,
  );
  const filtersSelected = checkboxFilters.reduce(
    (acc: CheckboxOptionSimpleType[], group) => [
      ...acc,
      ...group.options.filter((opt) => opt.isSelected),
    ],
    [],
  );

  const handleSendAllInvites = () => {
    if (ownedInvites > 0) {
      notifyResendPendingInvites({ inviter: 'me' });
    } else {
      notifyResendPendingInvites({ inviter: 'all' });
      setHasSendAllInvites(true);
    }
    setHasSendAllMyInvites(true);
  };

  const onClickResendPendingInvites = () => {
    notifyResendPendingInvites({ inviter: 'all' });
    setHasSendAllMyInvites(true);
    setHasSendAllInvites(true);
    setShowPendingInvitesOptions(false);
  };
  const onClickExport = () => {
    const selection = [...vaultsSelection];
    const contributors = selection
      .map((vault) =>
        vault.collaboratorsSelected
          .filter((collaborator) => collaborator.type === 'contributor')
          .map((collaborator) => `${vault.id}::${collaborator.email}`)
          .join(','),
      )
      .filter((value) => value !== '')
      .join(',');
    const guests = selection
      .map((vault) =>
        vault.collaboratorsSelected
          .filter((collaborator) => collaborator.type === 'guest')
          .map((collaborator) => `${vault.id}::${collaborator.email}`)
          .join(','),
      )
      .filter((value) => value !== '')
      .join(',');

    notifyExportAll(contributors, guests);
  };

  const exportRemoveButton = () => (
    <>
      <Button
        buttonSize={ButtonSize.Small}
        buttonStyle={ButtonStyle.PrimaryGhost}
        onClick={onClickExport}
        className={styles.exportButton}
      >
        Export
      </Button>
      <div className={styles.exportRemoveSeparator} />
      <Button
        buttonSize={ButtonSize.Small}
        buttonStyle={ButtonStyle.RedGhost}
        onClick={() => {
          setRemoveCollaboratorsModalVisible(true);
        }}
        className={styles.removeButton}
      >
        Remove
      </Button>
    </>
  );

  const showExportRemoveMenu = () => (
    <Menu className={styles.exportRemoveMenu}>{exportRemoveButton()}</Menu>
  );

  const getCheckboxState = () => {
    const vaultsSelected = vaultsSelection.filter((vault) => vault.selected);
    if (vaultsSelected.length > 0 && vaultsSelected.length < vaults.length)
      return null;
    return vaultsSelected.length === vaults.length && vaults.length > 0;
  };

  const notifyCollaboratorRemoved = (
    vault_id: number,
    collaborator_email: string,
  ) => {
    const vaultIndex = vaultsCollaborators.findIndex(
      (vault) => vault.id === vault_id,
    );
    if (vaultIndex === -1) return;

    const newVaultsCollaborators = [...vaultsCollaborators];
    newVaultsCollaborators[vaultIndex].collaborators = newVaultsCollaborators[
      vaultIndex
    ].collaborators.filter((c) => c.email !== collaborator_email);
    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_VAULTS,
      vaultsCollaborators: newVaultsCollaborators,
    });
    clearSelection();
    setRemoveCollaboratorsModalVisible(false);
    collaboratorsDispatch({
      type: collaboratorsActions.SET_REFETCH_COLLABORATORS,
      refetchCollaborators: true,
    });
  };

  const handleChangePage = (page: number) => {
    const newPageNum = page;

    const newQueryParamsCopy = { ...queryParams };
    newQueryParamsCopy.pageNum = newPageNum;

    collaboratorsDispatch({
      type: collaboratorsActions.UPDATE_QUERY_PARAMS,
      queryParams: newQueryParamsCopy,
    });
  };

  const showInviteMenu = () => (
    <Menu className={styles.inviteMenu} width={styles.inviteMenuWith}>
      <button
        className={styles.inviteMenuItem}
        type="button"
        onClick={() => {
          notifyShowInviteToRecordProcess(InvitationStep.inviteContributors);
        }}
      >
        <div className={styles.inviteMenuItemIcon}>
          <UserContributorIcon />
        </div>
        <div className={styles.inviteMenuItemText}>
          <span className="text__body__bold__medium__gray700">
            Invite editor
          </span>
          <p className="text__body__regular__small__gray700">
            Manage all aspects of the vault, except subscription and payment.
          </p>
        </div>
      </button>
      <div className={styles.inviteSeparator} />
      <button
        className={styles.inviteMenuItem}
        type="button"
        onClick={() => {
          notifyShowInviteToRecordProcess(InvitationStep.inviteGuests);
        }}
      >
        <div className={styles.inviteMenuItemIcon}>
          <UserGuestIcon />
        </div>
        <div className={styles.inviteMenuItemText}>
          <span className="text__body__bold__medium__gray700">
            Invite contributor
          </span>
          <p className="text__body__regular__small__gray700">
            Can only upload or record content - no editing or viewing.
          </p>
        </div>
      </button>
    </Menu>
  );

  const showPendingInvitesMenu = () => (
    <div className={styles.pendingInvitesMenu}>
      <Menu
        className={styles.pendingInvitesMenuContent}
        width={styles.menuWith}
      >
        <button
          type="button"
          className="text__body__regular__small__gray700"
          onClick={notifySeeMyPendingInvites}
        >
          See my pending invites
        </button>
        {ownedInvites > 0 && !hasSendAllInvites && (
          <>
            <div className={styles.pendingInvitesOptionsSeparator} />
            <button
              type="button"
              className="text__body__regular__small__gray700"
              onClick={onClickResendPendingInvites}
            >
              Resend all invites
            </button>
          </>
        )}
      </Menu>
    </div>
  );

  return (
    <div className={styles.container}>
      {removeCollaboratorsModalVisible && (
        <RemoveCollaboratorsModal
          vaultsSelection={vaultsSelection}
          amountSelected={amountSelected}
          onClose={() => setRemoveCollaboratorsModalVisible(false)}
          notifyCollaboratorRemoved={notifyCollaboratorRemoved}
        />
      )}
      <div className={styles.tableHeader}>
        <div className={styles.selectPage}>
          <ThreeStatesCheckbox
            itemKey="select-all"
            onChange={onSelectAll}
            checkValue={getCheckboxState()}
            disabled={vaults.length === 0}
          />
          <span
            className={classnames(
              styles.noWrap,
              isMobile
                ? 'text__body__semi__bold__tiny__gray800'
                : 'text__body__semi__bold__small__gray800',
            )}
          >
            Select all
          </span>
          {/* TODO: Define this in outside components */}
          {isAnySelected && (
            <>
              <div className={styles.selectionActions}>
                <div className={styles.selectionSeparator} />
                <div className={styles.amountSelected}>
                  <span
                    className={classnames(
                      styles.noWrap,
                      isMobile
                        ? 'text__body__semi__bold__tiny__gray800'
                        : 'text__body__semi__bold__small__gray800',
                    )}
                  >
                    {amountSelected}{' '}
                    <span className="text__body__regular__small__gray700">
                      selected
                    </span>
                  </span>
                  <button
                    type="button"
                    className={styles.selectionCrossIcon}
                    onClick={clearSelection}
                  >
                    <CrossIcon />
                  </button>
                </div>
                {exportRemoveButton()}
              </div>
              <div className={styles.inviteButtonContainer}>
                <button
                  type="button"
                  className={styles.selectionThreeDots}
                  onClick={() => setExportRemoveMenuVisible((prev) => !prev)}
                >
                  <ThreeDots />
                </button>
                {exportRemoveMenuVisible && showExportRemoveMenu()}
              </div>
            </>
          )}
          {!isAnySelected &&
            filtersSelected.length > 0 &&
            filtersSelected.length < 17 && (
              <div className={styles.filtersAppliedBadge}>
                <span className="text__body__bold__small__brand500">
                  {filtersSelected.length}
                </span>
                <span className="text__body__regular__small__gray700">
                  filters applied
                </span>
              </div>
            )}
        </div>
        <div className={styles.inviteButtonContainer} ref={inviteContainerRef}>
          <Button
            className={styles.inviteButton}
            buttonSize={ButtonSize.Small}
            onClick={() => setInviteMenuVisible((prev) => !prev)}
          >
            <PlusIcon />
            Invite
          </Button>
          {inviteMenuVisible && showInviteMenu()}
        </div>
      </div>
      {(totalPendingInvites > 0 || ownedInvites > 0) && (
        <div className={styles.pendingInvites}>
          <div className={styles.pendingInvitesLeftSection}>
            <ClockIcon />
            <span className="text__body__bold__medium__gray700">
              {totalPendingInvites} Pending invites&ensp; • &ensp;
              <span className="text__body__regular__medium__gray700">
                {ownedInvites} Sent by me
              </span>
            </span>
          </div>
          <div className={styles.pendingInvitesRightSection}>
            <Button
              buttonSize={ButtonSize.Small}
              onClick={handleSendAllInvites}
              disabled={hasSendAllMyInvites}
            >
              {sendAllInvitesButtonText}
            </Button>
            <div ref={pendingInvitesMenuRef}>
              <Button
                buttonSize={ButtonSize.Small}
                buttonStyle={ButtonStyle.PrimaryGhost}
                onClick={() => {
                  setShowPendingInvitesOptions(!showPendingInvitesOptions);
                }}
              >
                <ThreeDots />
              </Button>
              {showPendingInvitesOptions && showPendingInvitesMenu()}
            </div>
          </div>
        </div>
      )}
      {vaults.length === 0 && <NoCollaboratorsFound />}
      {vaults.map((vault) => (
        <VaultCollaborators
          vault={vault}
          user={user}
          key={vault.id}
          vaultSelection={vaultsSelection.find(
            (vaultSelection) => vaultSelection.id === vault.id,
          )}
          onSelectVault={onSelectVault}
          refreshKey={peopleRefreshKey}
        />
      ))}
      <PaginationSelect
        paginationInfo={pagination}
        notifyGetMoreVaults={handleChangePage}
      />
    </div>
  );
};

export { CollaboratorsContent };
